import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BaseChartDirective } from 'ng2-charts';
import {
  ChartConfiguration,
  ChartData,
  ChartType,
  Chart,
  Plugin,
} from 'chart.js';

interface ComplianceMetric {
  category: string;
  dlp: number;
  da: number;
  ai: number;
  transfer: number;
}

interface MitigationRow {
  setArea: string;
  high: number;
  medium: number;
  low: number;
  total: number;
}

interface RiskItem {
  name: string;
  score: number;
  scoreType: 'warning' | 'medium' | 'high' | 'low';
  rag: string;
  description: string;
  update: string;
}

interface IncidentData {
  category: string;
  count?: number;
  value: number;
  color: string;
}

// Custom plugin to display totals on top of stacked bars
const stackedBarTotalsPlugin: Plugin = {
  id: 'stackedBarTotals',
  afterDatasetsDraw(chart: any) {
    const { ctx, data, scales } = chart;

    if (chart.config.type === 'bar' && scales.x?.options?.stacked) {
      const xScale = scales.x;
      const yScale = scales.y;

      // Calculate totals for each bar
      const totals: number[] = [];
      const datasetCount = data.datasets.length;
      const dataLength = data.datasets[0]?.data.length || 0;

      for (let i = 0; i < dataLength; i++) {
        let total = 0;
        for (let j = 0; j < datasetCount; j++) {
          const value = data.datasets[j].data[i] as number;
          total += value || 0;
        }
        totals.push(total);
      }

      // Draw totals on top of each stacked bar
      ctx.save();
      ctx.font = '300 12px Arial';
      ctx.fillStyle = '#4A4A4A';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      totals.forEach((total, index) => {
        const x = xScale.getPixelForValue(index);
        const y = yScale.getPixelForValue(total);
        ctx.fillText(total.toString(), x, y - 5);
      });

      ctx.restore();
    }
  },
};

@Component({
  selector: 'app-main-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective],
  templateUrl: './main-dashboard.component.html',
  styleUrls: ['./main-dashboard.component.scss'],
})
export class MainDashboardComponent implements OnInit, OnDestroy {
  executiveSummary: string = '';

  complianceMetrics: ComplianceMetric[] = [
    { category: 'All', dlp: 100, da: 100, ai: 100, transfer: 100 },
    { category: 'Ops', dlp: 100, da: 100, ai: 100, transfer: 100 },
    { category: 'R&D', dlp: 100, da: 100, ai: 100, transfer: 100 },
    { category: 'Commercial', dlp: 100, da: 100, ai: 100, transfer: 100 },
    { category: 'Other', dlp: 100, da: 100, ai: 100, transfer: 100 },
  ];

  mitigationHealthData: MitigationRow[] = [
    { setArea: 'OPS', high: 40, medium: 21, low: 41, total: 102 },
    { setArea: 'R&D', high: 91, medium: 46, low: 51, total: 188 },
    { setArea: 'Commercial', high: 24, medium: 80, low: 63, total: 167 },
    { setArea: 'Other', high: 36, medium: 71, low: 28, total: 135 },
    { setArea: 'Other', high: 24, medium: 80, low: 63, total: 167 },
  ];

  riskItems: RiskItem[] = [
    {
      name: 'Internal Data Disclosure',
      score: 10,
      scoreType: 'warning',
      rag: 'Text Information',
      description: 'Text Information',
      update: 'Text Information',
    },
    {
      name: 'External Data Disclosure',
      score: 8,
      scoreType: 'high',
      rag: 'Text Information',
      description: 'Text Information',
      update: 'Text Information',
    },
    {
      name: 'Non-Compliance with Contractual obligations',
      score: 7,
      scoreType: 'high',
      rag: 'Text Information',
      description: 'Text Information',
      update: 'Text Information',
    },
    {
      name: 'Non-Compliance with AI Regs',
      score: 5,
      scoreType: 'medium',
      rag: 'Text Information',
      description: 'Text Information',
      update: 'Text Information',
    },
    {
      name: 'Non-Compliance with AI Regs',
      score: 3,
      scoreType: 'low',
      rag: 'Text Information',
      description: 'Text Information',
      update: 'Text Information',
    },
  ];

  incidentData: IncidentData[] = [
    { category: 'Cat 1', value: 100, color: '#9A3374' },
    { category: 'Cat 2', value: 50, color: '#F3BC33' },
    { category: 'Cat 3', value: 90, color: '#D0DE33' },
    { category: 'Cat 4', value: 60, color: '#336084' },
    { category: 'Cat 5', value: 30, color: '#DA338C' },
    { category: 'Cat 6', value: 65, color: '#86DCE6' },
    { category: 'Cat 7', value: 82, color: '#8A7098' },
  ];

  controlsTestedData = [
    { name: 'R&D', failed: 60, partiallyPassed: 20, passed: 16 },
    { name: 'Ops', failed: 50, partiallyPassed: 35, passed: 40 },
    { name: 'EDO', failed: 20, partiallyPassed: 55, passed: 7 },
    { name: 'HR', failed: 10, partiallyPassed: 30, passed: 27 },
  ];

  // Incident Management Bar Chart
  public incidentChartType: ChartType = 'bar';
  public incidentChartData: ChartConfiguration['data'] = {
    labels: this.incidentData.map((d) => d.category),
    datasets: [
      {
        data: this.incidentData.map((d) => d.value),
        backgroundColor: this.incidentData.map((d) => d.color),
        borderWidth: 0,
        barThickness: 50,
      },
    ],
  };
  public incidentChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { stepSize: 20 },
      },
      x: {
        grid: { display: false },
      },
    },
  };

  // Key Control Assurance Stacked Bar Chart
  public controlsChartType: ChartType = 'bar';
  public controlsChartData: ChartConfiguration['data'] = {
    labels: this.controlsTestedData.map((d) => d.name),
    datasets: [
      {
        label: 'Passed',
        data: this.controlsTestedData.map((d) => d.passed),
        backgroundColor: '#91ba93',
        barThickness: 36,
      },
      {
        label: 'Partially Passed',
        data: this.controlsTestedData.map((d) => d.partiallyPassed),
        backgroundColor: '#ffd56c',
        barThickness: 36,
      },
      {
        label: 'Failed',
        data: this.controlsTestedData.map((d) => d.failed),
        backgroundColor: '#dd6c6c',
        barThickness: 36,
      },
    ],
  };
  public controlsChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { stepSize: 20 },
      },
    },
  };

  // Growth Incident Line Chart
  public growthChartType: ChartType = 'line';
  public growthChartData: ChartConfiguration['data'] = {
    labels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
    ],
    datasets: [
      {
        label: 'Failed',
        data: [21, 27, 36, 42, 48, 65, 82, 90, 93, 100],
        borderColor: '#D32F2F',
        backgroundColor: 'rgba(211, 47, 47, 0.1)',
        fill: false,
        tension: 0,
        pointBackgroundColor: '#D32F2F',
        pointRadius: 4,
      },
      {
        label: 'Partially passed',
        data: [21, 24, 36, 41, 45, 58, 63, 68, 73, 78],
        borderColor: '#F0AB00',
        backgroundColor: 'rgba(240, 171, 0, 0.1)',
        fill: false,
        tension: 0,
        pointBackgroundColor: '#F0AB00',
        pointRadius: 4,
      },
      {
        label: 'Passed',
        data: [21, 22, 36, 45, 50, 60, 73, 77, 85, 92],
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        fill: false,
        tension: 0,
        pointBackgroundColor: '#4CAF50',
        pointRadius: 4,
      },
    ],
  };
  public growthChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { stepSize: 20 },
      },
      x: {
        grid: { display: false },
      },
    },
  };

  ngOnInit(): void {
    // Register the custom plugin for stacked bar totals
    Chart.register(stackedBarTotalsPlugin);
  }

  ngOnDestroy(): void {
    // Unregister the plugin when component is destroyed
    Chart.unregister(stackedBarTotalsPlugin);
  }

  getScoreBadgeClass(type: string): string {
    const classes: { [key: string]: string } = {
      warning: 'score-badge-warning',
      medium: 'score-badge-medium',
      high: 'score-badge-high',
      low: 'score-badge-low',
    };
    return classes[type] || '';
  }

  getScoreIcon(type: string): string {
    const icons: { [key: string]: string } = {
      warning: 'assets/icons/risk-warning.svg',
      medium: 'assets/icons/risk-medium.svg',
      high: 'assets/icons/risk-high.svg',
      low: 'assets/icons/risk-low.svg',
    };
    return icons[type] || '';
  }

  getBarHeight(value: number, max: number = 100): string {
    return `${(value / max) * 100}%`;
  }
}
